import { Schema } from "effect";
import { AccountId, ShopperAiIntegrationId } from "../../../ids/definitions";
import {
  AttentiveChannelData,
  AttentiveIntegrationType,
  ContactFormIntegrationChannelData,
  ContactFormIntegrationType,
  CustomEmailChannelData,
  CustomEmailIntegrationType,
  FacebookChannelData,
  FacebookIntegrationType,
  GmailChannelData,
  GmailIntegrationType,
  GorgiasSmsChannelData,
  GorgiasSmsIntegrationType,
  ImessageChannelData,
  ImessageIntegrationType,
  InstagramChannelData,
  InstagramIntegrationType,
  MobileMessageChannelData,
  MobileMessageIntegrationType,
  OutlookChannelData,
  OutlookIntegrationType,
  PostscriptChannelData,
  PostscriptIntegrationType,
  RedoChatAiChannelData,
  RedoChatAiIntegrationType,
  RedoChatFlowChannelData,
  RedoChatFlowIntegrationType,
  RedoChatLiveChannelData,
  RedoChatLiveIntegrationType,
  SmsPhoneNumberChannelData,
  SmsPhoneNumberIntegrationType,
  TelnyxSmsChannelData,
  TelnyxSmsIntegrationType,
  TelnyxVoiceChannelData,
  TelnyxVoiceIntegrationType,
  TiktokChannelData,
  TiktokIntegrationType,
} from "./channel-data";

export const ShopperAiIntegrationType = Schema.Union(
  GmailIntegrationType,
  OutlookIntegrationType,
  InstagramIntegrationType,
  FacebookIntegrationType,
  TiktokIntegrationType,
  AttentiveIntegrationType,
  ImessageIntegrationType,
  GorgiasSmsIntegrationType,
  SmsPhoneNumberIntegrationType,
  TelnyxSmsIntegrationType,
  TelnyxVoiceIntegrationType,
  MobileMessageIntegrationType,
  PostscriptIntegrationType,
  RedoChatAiIntegrationType,
  RedoChatFlowIntegrationType,
  RedoChatLiveIntegrationType,
  ContactFormIntegrationType,
  CustomEmailIntegrationType,
);
export type ShopperAiIntegrationType = typeof ShopperAiIntegrationType.Type;

// Base integration data shared by all integration types
const BaseIntegrationData = Schema.Struct({
  id: ShopperAiIntegrationId,
  accountId: AccountId,
  permissionsRevoked: Schema.Boolean,
});

// Individual integration response types (discriminated by integrationType)
const GmailIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: GmailIntegrationType,
  channelData: Schema.NullOr(GmailChannelData),
});

const OutlookIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: OutlookIntegrationType,
  channelData: Schema.NullOr(OutlookChannelData),
});

const FacebookIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: FacebookIntegrationType,
  channelData: Schema.NullOr(FacebookChannelData),
});

const InstagramIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: InstagramIntegrationType,
  channelData: Schema.NullOr(InstagramChannelData),
});

const AttentiveIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: AttentiveIntegrationType,
  channelData: Schema.NullOr(AttentiveChannelData),
});

const PostscriptIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: PostscriptIntegrationType,
  channelData: Schema.NullOr(PostscriptChannelData),
});

const TiktokIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: TiktokIntegrationType,
  channelData: Schema.NullOr(TiktokChannelData),
});

const RedoChatAiIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: RedoChatAiIntegrationType,
  channelData: Schema.NullOr(RedoChatAiChannelData),
});

const RedoChatFlowIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: RedoChatFlowIntegrationType,
  channelData: Schema.NullOr(RedoChatFlowChannelData),
});

const RedoChatLiveIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: RedoChatLiveIntegrationType,
  channelData: Schema.NullOr(RedoChatLiveChannelData),
});

const ImessageIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: ImessageIntegrationType,
  channelData: Schema.NullOr(ImessageChannelData),
});

const GorgiasSmsIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: GorgiasSmsIntegrationType,
  channelData: Schema.NullOr(GorgiasSmsChannelData),
});

const SmsPhoneNumberIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: SmsPhoneNumberIntegrationType,
  channelData: Schema.NullOr(SmsPhoneNumberChannelData),
});

const TelnyxSmsIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: TelnyxSmsIntegrationType,
  channelData: Schema.NullOr(TelnyxSmsChannelData),
});

const TelnyxVoiceIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: TelnyxVoiceIntegrationType,
  channelData: Schema.NullOr(TelnyxVoiceChannelData),
});

const MobileMessageIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: MobileMessageIntegrationType,
  channelData: Schema.NullOr(MobileMessageChannelData),
});

const ContactFormIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: ContactFormIntegrationType,
  channelData: Schema.NullOr(ContactFormIntegrationChannelData),
});

const CustomEmailIntegrationWithChannelDataResponse = Schema.Struct({
  ...BaseIntegrationData.fields,
  integrationType: CustomEmailIntegrationType,
  channelData: Schema.NullOr(CustomEmailChannelData),
});

// Response schema for integration with channel data (discriminated union by integrationType)
export const IntegrationWithChannelDataResponse = Schema.Union(
  GmailIntegrationWithChannelDataResponse,
  OutlookIntegrationWithChannelDataResponse,
  FacebookIntegrationWithChannelDataResponse,
  InstagramIntegrationWithChannelDataResponse,
  AttentiveIntegrationWithChannelDataResponse,
  PostscriptIntegrationWithChannelDataResponse,
  TiktokIntegrationWithChannelDataResponse,
  RedoChatAiIntegrationWithChannelDataResponse,
  RedoChatFlowIntegrationWithChannelDataResponse,
  RedoChatLiveIntegrationWithChannelDataResponse,
  ImessageIntegrationWithChannelDataResponse,
  GorgiasSmsIntegrationWithChannelDataResponse,
  SmsPhoneNumberIntegrationWithChannelDataResponse,
  TelnyxSmsIntegrationWithChannelDataResponse,
  TelnyxVoiceIntegrationWithChannelDataResponse,
  MobileMessageIntegrationWithChannelDataResponse,
  ContactFormIntegrationWithChannelDataResponse,
  CustomEmailIntegrationWithChannelDataResponse,
);
export type IntegrationWithChannelDataResponse =
  typeof IntegrationWithChannelDataResponse.Type;

export const IntegrationCursor = Schema.Struct({ id: ShopperAiIntegrationId });
export type IntegrationCursor = typeof IntegrationCursor.Type;

// Input for updating the default email/sms integrations on a contact form
// integration. Null values clear the default.
export const UpdateContactFormIntegrationInput = Schema.Struct({
  emailIntegrationId: Schema.NullOr(ShopperAiIntegrationId),
  smsIntegrationId: Schema.NullOr(ShopperAiIntegrationId),
});
export type UpdateContactFormIntegrationInput =
  typeof UpdateContactFormIntegrationInput.Type;
